/**
 * ArticleReader — /articles/:slug read-side page. Renders sanitized
 * HTML body, offers a "Listen To" TTS chip, and a 60-language
 * translation dropdown that hot-swaps the article body in place.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, Eye, Edit2, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ListenTo from "@/components/ListenTo";
import TranslateDropdown from "@/components/TranslateDropdown";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function ArticleReader() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(null);
  const [translation, setTranslation] = useState(null);

  useEffect(() => {
    let alive = true;
    setArticle(null);
    setTranslation(null);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/articles/${slug}`);
        if (alive) setArticle(data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || e.message || "Failed to load");
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const onTranslated = useCallback((tx) => { setTranslation(tx); }, []);
  const onReset = useCallback(() => setTranslation(null), []);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="article-error">
        <p className="text-sm text-red-600">{error}</p>
        <Link to="/articles" className="mt-4 inline-block text-sm text-blue-700 hover:underline">← Back to articles</Link>
      </div>
    );
  }
  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center text-slate-500" data-testid="article-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        <p className="mt-3 text-sm">Loading article…</p>
      </div>
    );
  }

  const displayed = translation || article;
  const currentLang = translation?.target_lang || article.language || "en";
  const isRTL = ["ar", "he", "fa", "ur"].includes(currentLang);
  const canEdit = user && (user.id === article.author_id || user.is_admin);

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-6 py-8" data-testid="article-reader">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/articles" className="text-xs uppercase tracking-widest text-slate-400 hover:text-blue-700 font-semibold inline-flex items-center gap-1" data-testid="article-reader-back">
          <ArrowLeft className="w-3 h-3" /> All articles
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <ListenTo articleId={article.id} lang={translation ? currentLang : null} testIdPrefix="article-listen" />
          <TranslateDropdown
            articleId={article.id}
            currentLang={translation ? currentLang : null}
            onTranslated={onTranslated}
            onReset={onReset}
          />
          {canEdit && (
            <Link
              to={`/articles/${article.slug}/edit`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-sm"
              data-testid="article-reader-edit"
            >
              <Edit2 className="w-3 h-3" /> Edit
            </Link>
          )}
        </div>
      </div>

      <div className="mt-4">
        {article.category && (
          <span className="inline-block text-[10px] uppercase tracking-widest font-bold text-blue-700 mb-2">
            {article.category.replace(/-/g, " ")}
          </span>
        )}
        <h1
          dir={isRTL ? "rtl" : "ltr"}
          className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900"
          data-testid="article-reader-title"
        >
          {displayed.title}
        </h1>
        {displayed.subtitle && (
          <p
            dir={isRTL ? "rtl" : "ltr"}
            className="mt-2 text-lg text-slate-600"
            data-testid="article-reader-subtitle"
          >
            {displayed.subtitle}
          </p>
        )}
        <div className="mt-3 flex items-center gap-3 text-xs text-slate-500 flex-wrap" data-testid="article-reader-meta">
          {article.author?.name && <span>By {article.author.name}</span>}
          {article.published_at && <span>· {new Date(article.published_at).toLocaleDateString()}</span>}
          {article.reading_time_min && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {article.reading_time_min} min read</span>}
          {typeof article.view_count === "number" && <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {article.view_count} views</span>}
          {translation && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 font-semibold">
              Translated · {currentLang.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      {article.cover_image_url && (
        <img
          src={article.cover_image_url}
          alt="Cover"
          className="mt-6 rounded-2xl max-h-[520px] w-full object-cover border border-slate-200"
          data-testid="article-reader-cover"
        />
      )}

      <article
        dir={isRTL ? "rtl" : "ltr"}
        className="pub-prose mt-6"
        data-testid="article-reader-body"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: displayed.body_html || "" }}
      />

      {article.tags?.length ? (
        <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="article-reader-tags">
          {article.tags.map((t) => (
            <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">#{t}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
